/**
 * JSON-LD structured data for rich results.
 * URLs use VITE_BASE_URL via absoluteUrl().
 */

import { absoluteUrl, getSiteOrigin } from "@/lib/seoConfig";
import { stripHtml } from "@/utils/textUtils";

function pruneUndefined(obj) {
  if (!obj || typeof obj !== "object") return obj;
  Object.keys(obj).forEach((key) => {
    if (obj[key] === undefined) delete obj[key];
    else if (obj[key] && typeof obj[key] === "object" && !Array.isArray(obj[key]))
      pruneUndefined(obj[key]);
  });
  return obj;
}

/** Book page path segment: prefer googleBooksId, then bookId, then Firestore id. */
export function bookPublicPath(book) {
  if (!book) return "";
  const id = book.googleBooksId || book.bookId || book.id;
  return id ? `/books/${id}` : "";
}

export function BookStructuredData({ book }) {
  if (!book) return null;

  const bookPath = bookPublicPath(book);
  const structuredData = pruneUndefined({
    "@context": "https://schema.org",
    "@type": "Book",
    name: book.title,
    author: {
      "@type": "Person",
      name: book.author,
    },
    image: book.coverUrl ? absoluteUrl(book.coverUrl) : undefined,
    description: stripHtml(book.description),
    isbn: book.isbn13 || book.isbn,
    bookFormat: "https://schema.org/EBook",
    inLanguage: book.language || "en",
    numberOfPages: book.pages,
    publisher: book.publisher
      ? {
          "@type": "Organization",
          name: book.publisher,
        }
      : undefined,
    datePublished: book.publishedDate,
    aggregateRating:
      book.rating && book.totalRatings > 0
        ? {
            "@type": "AggregateRating",
            ratingValue: book.rating,
            ratingCount: book.totalRatings,
            bestRating: 5,
            worstRating: 1,
          }
        : undefined,
    offers: {
      "@type": "Offer",
      availability: "https://schema.org/InStock",
      price: "0",
      priceCurrency: "GBP",
    },
    genre: book.genres ? book.genres.join(", ") : undefined,
    url: bookPath ? absoluteUrl(bookPath) : undefined,
  });

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
    />
  );
}

export function PersonStructuredData({ user }) {
  if (!user) return null;

  const structuredData = pruneUndefined({
    "@context": "https://schema.org",
    "@type": "Person",
    name: user.displayName,
    alternateName: user.username,
    description: user.bio,
    image: user.profileImage ? absoluteUrl(user.profileImage) : undefined,
    url: absoluteUrl(`/users/${user.username}`),
    sameAs: user.socialLinks
      ? Object.values(user.socialLinks).filter(Boolean)
      : undefined,
  });

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
    />
  );
}

export function ItemListStructuredData({ list }) {
  if (!list) return null;

  const structuredData = pruneUndefined({
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: list.name,
    description: list.description,
    numberOfItems: list.books?.length || 0,
    creator: list.creator?.username
      ? {
          "@type": "Person",
          name: list.creator?.displayName,
          url: absoluteUrl(`/users/${list.creator.username}`),
        }
      : undefined,
    itemListElement:
      list.books?.slice(0, 10).map((book, index) => {
        const path = book.googleBooksId
          ? `/books/${book.googleBooksId}`
          : book.id
            ? `/books/${book.id}`
            : "";
        return {
          "@type": "ListItem",
          position: index + 1,
          item: pruneUndefined({
            "@type": "Book",
            name: book.title,
            author: book.author
              ? { "@type": "Person", name: book.author }
              : undefined,
            image: book.coverUrl ? absoluteUrl(book.coverUrl) : undefined,
            url: path ? absoluteUrl(path) : undefined,
          }),
        };
      }) || [],
  });

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
    />
  );
}

export function OrganizationStructuredData() {
  const origin = getSiteOrigin();
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "NovelNet",
    url: origin,
    logo: absoluteUrl("/favicon.ico"),
    description:
      "Track your reading progress, discover new books, and connect with fellow readers. The modern alternative to Goodreads.",
    sameAs: [
      "https://www.instagram.com/novelnet.co.uk",
      "https://www.tiktok.com/@novelnet.co.uk",
      "https://bsky.app/profile/novelnet.co.uk",
    ],
    contactPoint: {
      "@type": "ContactPoint",
      email: "support@novelnet.co.uk",
      contactType: "Customer Support",
    },
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
    />
  );
}

export function WebSiteStructuredData() {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "NovelNet",
    url: getSiteOrigin(),
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${getSiteOrigin()}/search?q={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
    />
  );
}

export function WebApplicationStructuredData() {
  const structuredData = pruneUndefined({
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "NovelNet",
    url: getSiteOrigin(),
    applicationCategory: "LifestyleApplication",
    operatingSystem: "Web",
    description:
      "Social reading and book tracking: shelves, lists, reviews, goals, and discovery.",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "GBP",
    },
  });

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
    />
  );
}

export function ReviewStructuredData({ review, book }) {
  if (!review || !book) return null;

  const structuredData = pruneUndefined({
    "@context": "https://schema.org",
    "@type": "Review",
    itemReviewed: {
      "@type": "Book",
      name: book.title,
      author: book.author
        ? { "@type": "Person", name: book.author }
        : undefined,
      image: book.coverUrl ? absoluteUrl(book.coverUrl) : undefined,
    },
    reviewRating: {
      "@type": "Rating",
      ratingValue: review.rating,
      bestRating: 5,
      worstRating: 1,
    },
    author: review.user?.displayName
      ? {
          "@type": "Person",
          name: review.user.displayName,
        }
      : undefined,
    reviewBody: review.text,
    datePublished: review.createdAt,
  });

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
    />
  );
}

export function BreadcrumbStructuredData({ items }) {
  if (!items || items.length === 0) return null;

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: absoluteUrl(item.url),
    })),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
    />
  );
}
